// Mirror of proxy-server/lifecycle/confidence.js (CommonJS) — keep in sync.
//
// Maps the existing matchTier / matchedBy strings already emitted across
// AdLifecycleView.jsx (phases.prebid.matchedBy, phases.amazon.matchTier,
// phases.impression.matchTier, phases.click.matchTier) onto exactly three
// confidence tiers, aligned to the redundancy engine's deterministic/heuristic
// language:
//
//   proven   (≈ deterministic) — hard token: cache_id, amzn_b, xai, sai, sigh,
//                                exact nurl/lurl/burl URL, correlator
//   inferred (≈ heuristic)     — soft signal + timing: gpid, bidder_price,
//                                prebid_size/prebid_timing, a3p buyer, adapter
//   guessed                    — timing / adUnit only: timing, time, adUnit,
//                                proximity, single, size_key, size_req
//
// Pure. No dependencies. Never throws — unknown input falls back to 'guessed'
// (most conservative), matching the validator convention "degrade gracefully".
//
// ── SYNC CONTRACT ───────────────────────────────────────────────────────────
// The proxy's copy (proxy-server/lifecycle/confidence.js, CommonJS) MUST
// carry the identical PROVEN/INFERRED/GUESSED sets, with exactly one
// documented exception: INFERRED_TOKENS here also contains 'manual' — a
// human disambiguating an ambiguous match via `manualOverrides`, persisted
// to localStorage (see AdLifecycleView.jsx). There is no server-side
// equivalent, so the proxy legitimately omits it. This module is the side
// actually exercised in production (AdLifecycleView.jsx / mediationLifecycle.js
// call tierFor() with real matchedBy/matchTier strings); the proxy's copy is
// currently unused ahead of the WP3 proxy-side correlation engine, but it
// still must not drift because WP3 will consume it.
//
// Enforced by proxy-server/lifecycle/confidence.sync.test.js (runs under
// `node --test` on the proxy side — Jest/CRA on this side can't require()
// proxy CommonJS cleanly, so the guard lives there and reads this file as
// text). If you add/remove a token on either side, update the other.
//
// 2026-08-12 drift fix: the sets had diverged — this file was missing
// speculative WP3-facing variants the proxy copy had already anticipated
// ('hb_bidder'/'hbbidder', 'hb_pb'/'hbpb', 'bidderprice', 'price',
// 'prebidsize', 'size', 'a3p_buyer'/'a3pbuyer', 'adapterlabel', 'ad_unit',
// 'window', 'time_window', 'timewindow'); the proxy copy was missing real,
// currently-produced matchToken strings this file already had ('token',
// 'impcorrelator', 'prebid_timing', 'stored_batch', 'size_key', 'size_req').
// Both were reconciled by union (see git history for the pre-fix sets). No
// tier-classification behavior changed for any token either file already
// classified explicitly.

const PROVEN_TOKENS = new Set([
  'hb_cache_id', 'cache_id', 'prebidid', 'prebid_id',
  'amzn_b', 'amznslots', 'amzn_bid', 'token',
  'xai', 'sai', 'sigh', 'impcorrelator', 'correlator',
  'impurl', // captured ping URL == one the GAM response imp_urls promised
  'nurl', 'lurl', 'burl', 'exact_url', 'exacturl',
]);

const INFERRED_TOKENS = new Set([
  'gpid', 'bidder_price', 'bidderprice', 'price',
  'prebid_size', 'prebidsize', 'prebid_timing', 'stored_batch', 'size',
  'a3p', 'a3p_buyer', 'a3pbuyer', 'a3p+timing',
  'adapter', 'adapterlabel', 'hb_bidder', 'hbbidder', 'hb_pb', 'hbpb',
  'sdktelemetry', // Liftoff SDK impression event, time and size gated, no hard token
  // 'manual' is intentionally frontend-only — see sync-contract note above.
  'manual',
]);

const GUESSED_TOKENS = new Set([
  'timing', 'time', 'adunit', 'ad_unit', 'proximity', 'single',
  'size_key', 'size_req', 'window', 'time_window', 'timewindow',
]);

const normalize = (v) => String(v == null ? '' : v).trim().toLowerCase();

/**
 * tierFor(matchToken) -> 'proven' | 'inferred' | 'guessed'
 * Accepts a single matchTier/matchedBy string, a composite ("a3p+timing",
 * "xai|sai"), or a descriptive label. Returns the strongest recognized tier.
 */
export function tierFor(matchToken) {
  const norm = normalize(matchToken);
  if (!norm) return 'guessed';
  if (PROVEN_TOKENS.has(norm)) return 'proven';
  if (INFERRED_TOKENS.has(norm)) return 'inferred';
  if (GUESSED_TOKENS.has(norm)) return 'guessed';

  // Composite / compound strings — split and take the strongest part.
  const parts = norm.split(/[+|,/&\s]+/).filter(Boolean);
  if (parts.length > 1) {
    let best = null;
    for (const part of parts) {
      let tier = null;
      if (PROVEN_TOKENS.has(part)) tier = 'proven';
      else if (INFERRED_TOKENS.has(part)) tier = 'inferred';
      else if (GUESSED_TOKENS.has(part)) tier = 'guessed';
      if (tier === 'proven') return 'proven';
      if (tier === 'inferred' && best !== 'proven') best = 'inferred';
      if (tier === 'guessed' && !best) best = 'guessed';
    }
    if (best) return best;
  }

  // Substring fallback for unrecognized-but-descriptive labels.
  if (/sigh|nurl|lurl|burl|xai|sai|cache_id|amzn_b|amznslots|correlator|exact/.test(norm)) {
    return 'proven';
  }
  if (/a3p|adapter|gpid|price|size|hb_bidder|hb_pb/.test(norm)) {
    return 'inferred';
  }
  return 'guessed';
}

export default { tierFor };
